import React from 'react';
import { JotBillIcon } from '@jotpsych/ui/icons/JotBillIcon';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Tooltip } from '@mui/material';

import type { EncounterIdentityBadge } from './types';

export interface EncounterIdentityBadgesProps {
  badges?: EncounterIdentityBadge[] | null;
  size?: number;
  /**
   * When provided, the coverage-gap badge becomes clickable and invokes this
   * to open a resolution path (the patient's insurance editor). Kept as a
   * callback so this component stays presentational.
   */
  onCoverageGapClick?: () => void;
}

function isJotBillBadge(badge: EncounterIdentityBadge) {
  return badge.icon === 'jotbill' || badge.key === 'jotbill';
}

function isCoverageGapBadge(badge: EncounterIdentityBadge) {
  return badge.key === 'coverage_gap';
}

function isRenderableBadge(badge: EncounterIdentityBadge) {
  return isJotBillBadge(badge) || isCoverageGapBadge(badge);
}

function EncounterIdentityBadgeIcon({
  badge,
  size,
}: {
  badge: EncounterIdentityBadge;
  size: number;
}) {
  if (isJotBillBadge(badge)) {
    return (
      <JotBillIcon
        aria-label={badge.label}
        fontSize="small"
        sx={{ width: size, height: size }}
      />
    );
  }

  // Coverage gap (GDN-2411): the encounter's date of service has no plan in
  // force — the claim will deny. Amber, backend-authored tooltip.
  if (isCoverageGapBadge(badge)) {
    return (
      <WarningAmberIcon
        aria-label={badge.label}
        fontSize="small"
        sx={{ width: size, height: size, color: 'warning.main' }}
      />
    );
  }

  return null;
}

export function EncounterIdentityBadges({
  badges,
  size = 16,
  onCoverageGapClick,
}: EncounterIdentityBadgesProps) {
  const visibleBadges = badges?.filter(badge => isRenderableBadge(badge)) ?? [];

  if (!visibleBadges.length) {
    return null;
  }

  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
      {visibleBadges.map(badge => {
        const clickable = isCoverageGapBadge(badge) && Boolean(onCoverageGapClick);
        const tooltip = clickable
          ? `${badge.tooltip ?? badge.label} Click to open the patient's insurance.`
          : badge.tooltip ?? badge.label;
        // stopPropagation: this badge often sits inside a clickable row/link —
        // resolving the gap must not also navigate.
        const activate = (event: React.SyntheticEvent) => {
          event.stopPropagation();
          event.preventDefault();
          onCoverageGapClick?.();
        };
        return (
          <Tooltip key={badge.key} title={tooltip} placement="top" arrow>
            <Box
              component="span"
              {...(clickable
                ? {
                    role: 'button',
                    tabIndex: 0,
                    'aria-label': `${badge.label} — update insurance`,
                    onClick: activate,
                    onKeyDown: (event: React.KeyboardEvent) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        activate(event);
                      }
                    },
                  }
                : {})}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                ...(clickable && {
                  cursor: 'pointer',
                  borderRadius: '4px',
                  p: 0.25,
                  m: -0.25,
                  '&:hover': { bgcolor: 'action.hover' },
                  '&:focus-visible': {
                    outline: theme => `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 1,
                  },
                }),
              }}
            >
              <EncounterIdentityBadgeIcon badge={badge} size={size} />
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}
